import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { useEffect, useState } from "react";
import { formMappings } from "../../../../config/formMappings";
import { getFormEntries } from "../../../../services/apiService";
import { FormEntry } from "../../../../types/types";

interface TotalTableRevenueProps {
  v2Entries?: FormEntry[];
}

export default function TotalTableRevenue({
  v2Entries: propV2Entries,
}: TotalTableRevenueProps) {
  const entries = useSelector((state: RootState) => state.formEntries.entries);
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const isVersion1 = formMapping?.version === "1";
  const bazaarTableMapping =
    formMapping && "bazaarTable" in formMapping
      ? formMapping.bazaarTable?.fieldMappings
      : undefined;
  const [localV2Entries, setLocalV2Entries] = useState<FormEntry[]>([]);

  // Only make API call if v2Entries prop is not provided
  useEffect(() => {
    if (
      propV2Entries === undefined &&
      !isVersion1 &&
      formMapping &&
      "bazaarTable" in formMapping &&
      formMapping.bazaarTable?.formId
    ) {
      // Make API call with form ID from bazaarTable mapping
      getFormEntries(formMapping.bazaarTable.formId)
        .then((data) => {
          setLocalV2Entries(data);
        })
        .catch((error) => {
          console.error("Error fetching form entries for form ID:", error);
        });
    }
  }, [propV2Entries, isVersion1, formMapping]);

  // Use prop if provided, otherwise use local state
  const v2Entries =
    propV2Entries !== undefined ? propV2Entries : localV2Entries;

  // Calculate total revenue from table bookings (only paid entries)
  const totalTableRevenue = (isVersion1 ? entries : v2Entries)
    .filter((entry) => {
      if (isVersion1) {
        // For v1, check if they need a table and if they're paid
        const needsTable =
          entry.meta["Will you be needing a table for bazaar?"] === "Yes";
        const paidStatus =
          entry.meta["Paid?"] ||
          entry.meta[formMapping?.fieldMappings?.paid || ""] ||
          "No";
        return needsTable && paidStatus === "Yes";
      } else {
        // For v2+, check registeredForConference and paid status
        if (!bazaarTableMapping) return false;
        const registeredField =
          entry.meta[bazaarTableMapping.registeredForConference] ||
          entry.meta["Will you be needing a table for bazaar?"];
        if (registeredField !== "Yes") return false;

        const paidStatusRaw =
          entry.meta[bazaarTableMapping.paid] || entry.meta["Paid?"] || "No";
        const paidStatus = Array.isArray(paidStatusRaw)
          ? paidStatusRaw[0]
          : paidStatusRaw;
        return paidStatus === "Yes";
      }
    })
    .reduce((total, entry) => {
      if (isVersion1) {
        // For v1, calculate: table count * $25
        const tableCountStr =
          entry.meta["How many tables do you need? ($25/per table)"] || "0";
        const tableCount = parseInt(
          Array.isArray(tableCountStr) ? tableCountStr[0] : tableCountStr,
          10
        );
        return total + (isNaN(tableCount) ? 0 : tableCount * 25);
      } else {
        // For v2+, use paidAmount field from bazaarTable mapping
        if (!bazaarTableMapping) return total;
        const paidAmountStr =
          entry.meta[bazaarTableMapping.paidAmount] ||
          entry.meta["p2l9j3-value"] ||
          "0";
        const paidAmount = parseFloat(
          Array.isArray(paidAmountStr) ? paidAmountStr[0] : paidAmountStr
        );
        return total + (isNaN(paidAmount) ? 0 : paidAmount);
      }
    }, 0);

  return (
    <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6">
      <dt className="truncate text-sm font-medium text-gray-500">
        Table Revenue
      </dt>
      <dd className="mt-1 text-3xl font-semibold tracking-tight text-gray-900">
        $
        {totalTableRevenue.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </dd>
    </div>
  );
}
